/**
 * waap-chart-widgets.tsx — Individual chart widget components for WAAP analytics.
 *
 * Renders the four chart kinds emitted by the BE analytics pipeline
 * (number / pie / table / timeseries) in a consistent card-based layout.
 * Shared by waap-analytics-board.tsx and any future WAAP chart surfaces.
 */

import { useMemo } from 'react';
import { Card, CardBody, CardHeader, CardTitle } from '@vnetwork/ui/primitives';
import { DataTable, type ColumnDef } from '@vnetwork/ui/data';
import type {
  WaapChart,
  WaapNumberChart,
  WaapPieChart,
  WaapTableChart,
  WaapTimeseriesChart,
} from '@/api/act-as-waap';

/* ── Individual kind renderers ───────────────────────────────────────────── */

export function NumberWidget({ chart }: { chart: WaapNumberChart }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>{chart.title}</CardTitle>
      </CardHeader>
      <CardBody>
        {chart.error ? (
          <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-xs)' }}>Unavailable</span>
        ) : (
          <span style={{ fontSize: 'var(--vn-font-size-2xl)', fontWeight: 700 }}>
            {chart.value == null ? '—' : chart.value.toLocaleString()}
            {chart.unit ? (
              <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 400, marginLeft: 'var(--vn-space-1)', color: 'var(--vn-fg-muted)' }}>
                {chart.unit}
              </span>
            ) : null}
          </span>
        )}
      </CardBody>
    </Card>
  );
}

export function PieWidget({ chart }: { chart: WaapPieChart }) {
  const slices = chart.slices ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>{chart.title}</CardTitle>
      </CardHeader>
      <CardBody>
        {chart.error || slices.length === 0 ? (
          <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-xs)' }}>
            {chart.error || 'No data'}
          </span>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            {slices.slice(0, 8).map((s) => (
              <div
                key={s.name}
                style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--vn-font-size-xs)', gap: 'var(--vn-space-3)' }}
              >
                <span style={{ color: 'var(--vn-fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.name}
                </span>
                <span style={{ color: 'var(--vn-fg-default)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                  {s.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export function TableWidget({ chart }: { chart: WaapTableChart }) {
  const cols = chart.columns ?? [];
  const rows = chart.rows ?? [];
  const columns = useMemo<ColumnDef<Record<string, unknown>, unknown>[]>(
    () =>
      cols.map((c) => ({
        id: c.key,
        accessorKey: c.key,
        header: c.label,
        cell: ({ row }) => String(row.original[c.key] ?? '—'),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cols.map((c) => c.key).join(',')],
  );
  return (
    <Card style={{ gridColumn: '1 / -1' }}>
      <CardHeader>
        <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>{chart.title}</CardTitle>
      </CardHeader>
      <CardBody style={{ padding: 0 }}>
        {chart.error || rows.length === 0 ? (
          <div style={{ padding: 'var(--vn-space-4)', color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-xs)' }}>
            {chart.error || 'No data'}
          </div>
        ) : (
          <DataTable columns={columns} data={rows.slice(0, 20)} dense />
        )}
      </CardBody>
    </Card>
  );
}

export function TimeseriesWidget({ chart }: { chart: WaapTimeseriesChart }) {
  const series = chart.series ?? [];
  const data   = chart.data   ?? [];
  return (
    <Card style={{ gridColumn: '1 / -1' }}>
      <CardHeader>
        <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>{chart.title}</CardTitle>
      </CardHeader>
      <CardBody>
        {chart.error || data.length === 0 ? (
          <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-xs)' }}>
            {chart.error || 'No data'}
          </span>
        ) : (
          <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
            {series.map((s) => s.label).join(' · ')} — {data.length} data points
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function ChartWidget({ chart }: { chart: WaapChart }) {
  if (chart.kind === 'number')     return <NumberWidget chart={chart} />;
  if (chart.kind === 'pie')        return <PieWidget chart={chart} />;
  if (chart.kind === 'table')      return <TableWidget chart={chart} />;
  if (chart.kind === 'timeseries') return <TimeseriesWidget chart={chart} />;
  return null;
}

/* ── Chart grid (four-lane layout) ──────────────────────────────────────── */

/** Bucket chart widgets by render lane: scalars → wide timeseries → split
 *  pies/compact tables → full-width wide tables (>5 columns). */
function groupCharts(charts: WaapChart[]) {
  const numbers: WaapChart[] = [];
  const wide:    WaapChart[] = [];
  const split:   WaapChart[] = [];
  const full:    WaapChart[] = [];
  for (const c of charts) {
    if (c.kind === 'number') numbers.push(c);
    else if (c.kind === 'timeseries') wide.push(c);
    else if (c.kind === 'table' && (c.columns?.length ?? 0) > 5) full.push(c);
    else split.push(c);
  }
  return { numbers, wide, split, full };
}

/** Four-lane chart grid — mirrors Partner's WaapChartGrid layout. */
export function WaapChartGrid({ charts }: { charts: WaapChart[] }) {
  const g = useMemo(() => groupCharts(charts), [charts]);
  const numStyle   = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 'var(--vn-space-3)' } as const;
  const splitStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 'var(--vn-space-3)' } as const;
  const wideStyle  = { display: 'grid', gap: 'var(--vn-space-3)' } as const;
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {g.numbers.length > 0 && <div style={numStyle}>  {g.numbers.map((c) => <ChartWidget key={c.key} chart={c} />)}</div>}
      {g.wide.length   > 0 && <div style={wideStyle}>  {g.wide.map((c)    => <ChartWidget key={c.key} chart={c} />)}</div>}
      {g.split.length  > 0 && <div style={splitStyle}> {g.split.map((c)   => <ChartWidget key={c.key} chart={c} />)}</div>}
      {g.full.length   > 0 && <div style={wideStyle}>  {g.full.map((c)    => <ChartWidget key={c.key} chart={c} />)}</div>}
    </div>
  );
}
